import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import BadgeDelta from "components/BadgeDelta";
import Dot from "components/Dot";
import ValueUnit from "components/ValueUnit";
import useUnitParser from "hooks/useUnitParser";
import usePreferences from "hooks/usePreferences";
import UnitHelper from "helpers/UnitHelper";
import MetricHelpers from "helpers/MetricHelpers";
import { MetadataType } from "types/metadata";
import OneRepMaxChartHelpers, { ProgressBubbleEnum } from "../../../helpers/OneRepMaxChartHelpers";
import type { OneRepMaxProgressEntry } from "../../../helpers/OneRepMaxChartHelpers";
import useOneRepMaxChartWeightMetric from "../../../useOneRepMaxChartWeightMetric";
import useOneRepMaxChartColor from "../../../useOneRepMaxChartColor";
import { Root, ValueContainer } from "./components";
import useUnitNotation from "hooks/useUnitNotation";

interface OneRepMaxChartHeaderEntryProps {
  entry: OneRepMaxProgressEntry;
}

function OneRepMaxChartHeaderEntry(props: OneRepMaxChartHeaderEntryProps) {
  const { entry } = props;
  const { unitNotation } = useUnitNotation();
  const weightMetric = useOneRepMaxChartWeightMetric();
  const parseUnit = useUnitParser();
  const [preferences] = usePreferences();
  const { t } = useTranslation();

  const getLineColor = useOneRepMaxChartColor();
  const color = useMemo(() => getLineColor(entry.variant), [getLineColor, entry.variant]);

  const unit = useMemo(() => unitNotation(weightMetric), [unitNotation, weightMetric]);

  // Velocity based entries show the predicted range; manual entries show the single recorded value.
  const displayValue = useMemo(() => {
    if (entry.last.metadataType !== MetadataType.LOAD_VELOCITY_PROFILE) {
      return String(Math.round(parseUnit(entry.last.y, weightMetric).value));
    }
    const { min, max } = entry.last.measurement.predictedOneRepMaxRange;
    return `${Math.round(parseUnit(min, weightMetric).value)}-${Math.round(parseUnit(max, weightMetric).value)}`;
  }, [entry.last, parseUnit, weightMetric]);

  const value = useMemo(() => {
    if (!entry.variant) return displayValue;
    return t("charting.one_rep_max_header_variant_value", {
      variant: t("measures.variant_single_letter", { context: entry.variant }),
      value: displayValue,
    });
  }, [displayValue, entry.variant, t]);

  // The badge colour follows the progress state, as the line does.
  const delta = useMemo(() => {
    if (!entry.first) return null;

    // The raw difference is converted unrounded, as rounding each endpoint first can shift the result.
    const displayMetadata = preferences.imperialSystemEnabled
      ? weightMetric.displayMetadata?.imperial
      : weightMetric.displayMetadata?.metric;

    const change = UnitHelper.convert(
      entry.last.y - entry.first.y,
      MetricHelpers.getSourceUnit(weightMetric),
      displayMetadata?.unit
    );

    const difference =
      entry.state === ProgressBubbleEnum.OnTrack ? 0 : Math.sign(change) * Math.round(Math.abs(change));

    const value = Math.abs(difference);
    const direction = Math.sign(difference);
    const deltaType = OneRepMaxChartHelpers.getProgressStateDeltaType(entry.state);

    const sign = direction > 0 ? "+" : direction < 0 ? "-" : null;
    if (sign) {
      return { deltaType, label: `${sign}${value} ${unit}` };
    }
    return { deltaType, label: `${value} ${unit}` };
  }, [entry.first, entry.last.y, entry.state, preferences.imperialSystemEnabled, unit, weightMetric]);

  return (
    <Root>
      <ValueContainer>
        {entry.variant ? <Dot bgcolor={color} /> : null}
        <ValueUnit value={value} unit={unit} />
      </ValueContainer>
      {delta ? <BadgeDelta deltaType={delta.deltaType}>{delta.label}</BadgeDelta> : null}
    </Root>
  );
}

export default memo(OneRepMaxChartHeaderEntry);
